import React from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

const BODIES = {
  terms: {
    title: 'Terms of Service',
    content: [
      'Welcome to Concorde Financial Limited ("Company", "we", "our"). By accessing or using our services, you agree to be bound by these Terms of Service.',
      'The Company is licensed as an International Brokerage and Clearing House by the Anjouan Offshore Finance Authority (License N° L16195/CFL). The Company operates exclusively as a brokerage — all services provided fall within brokerage and clearing activities.',
      'You acknowledge that trading in financial instruments through your brokerage account involves risk and may result in the loss of your invested capital.',
      'You agree to comply with all applicable laws in your jurisdiction. Brokerage services are not offered where they would be contrary to local law.',
      'The Company reserves the right to refuse service, terminate brokerage accounts, and remove content at its sole discretion.',
      'For questions regarding these Terms, contact us at info@concordefinancial.com.',
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    content: [
      'This Privacy Policy explains how Concorde Financial Limited collects, uses, and protects personal data of its brokerage clients.',
      'We collect information you provide during brokerage account onboarding and KYC, including your name, contact details, identity documents, and biometric data (selfie).',
      'We use your data to provide brokerage services, comply with AML/KYC obligations, and prevent fraud. Data may be shared with our regulator, banking partners and service providers under strict confidentiality.',
      'You have the right to access, correct, and request deletion of your personal data subject to our regulatory retention obligations.',
      'For privacy inquiries, contact info@concordefinancial.com.',
    ],
  },
  aml: {
    title: 'KYC / AML Policy',
    content: [
      'Concorde Financial Limited operates a robust Anti-Money Laundering and Know-Your-Customer program applied to all brokerage relationships, consistent with international standards.',
      'All brokerage clients must complete identity verification (KYC) before engaging in currency conversion or withdrawal activity within their brokerage account. Business clients must submit enhanced due diligence documentation.',
      'The Company performs sanctions screening on all brokerage clients and monitors brokerage transactions for suspicious activity.',
      'Reports of suspicious activity are made to the relevant financial intelligence units.',
      'The Company reserves the right to freeze brokerage accounts and request additional information at any time.',
    ],
  },
  risk: {
    title: 'Risk Disclosure',
    content: [
      'Trading in financial instruments through a brokerage account carries significant risk. You may lose some or all of your invested capital.',
      'Past performance is not indicative of future results. Currency exchange rates applied within your brokerage account fluctuate and may impact the value of your funds.',
      'Ensure you fully understand the risks and seek independent financial advice if necessary before opening a brokerage account.',
    ],
  },
  license: {
    title: 'International Brokerage License',
    content: [
      'Concorde Financial Limited holds an International Brokerage and Clearing House License N° L16195/CFL issued by the Anjouan Offshore Finance Authority. This license authorises the Company to conduct brokerage activities only.',
      'The license was issued pursuant to Government Notice N° 005 of 2005 and remains valid until 3 November 2026.',
      'The license authorises brokerage services in foreign exchange, CFDs, equities, commodities and related order-execution, clearing and custody functions performed strictly within the scope of brokerage operations.',
      'The original apostilled license documentation is available upon request through our compliance department.',
    ],
    is_license: true,
  },
};

export default function DocumentDetailPage() {
  const { docId } = useParams();
  const { t } = useLanguage();
  const { company } = useOutletContext() || {};
  const doc = BODIES[docId] || { title: 'Document', content: ['Not found.'] };
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16">
      <Button asChild variant="ghost" size="sm"><Link to="/documents"><ArrowLeft className="h-4 w-4 mr-2" /> {t('common.back')}</Link></Button>
      <div className="mt-6">
        {doc.is_license && <div className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--brand-gold))]/40 bg-[hsl(var(--brand-gold))]/10 px-3 py-1 text-xs text-[hsl(var(--brand-navy))] mb-4"><ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--brand-gold))]" /> License N° L16195/CFL</div>}
        <h1 className="font-serif text-3xl sm:text-4xl text-[hsl(var(--brand-navy-2))]">{doc.title}</h1>
        <Card className="bg-white mt-6">
          <CardContent className="p-8 space-y-4 leading-7 text-[hsl(var(--foreground))]">
            {doc.content.map((p, i) => <p key={i}>{p}</p>)}
            <div className="pt-4 border-t text-sm text-[hsl(var(--muted-foreground))]">
              Concorde Financial Limited, {company?.address}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
